export type ImportSource = "provider" | "file";

export type ImportProvider =
  | "harmony"
  | "jsm"
  | "jira"
  | "freshservice"
  | "servicenow"
  | "servicenow-serviceaccount"
  | "solarwinds"
  | "zendesk"
  | "monday"
  | "sysaid";

export const IMPORT_PROVIDERS: { value: ImportProvider; label: string }[] = [
  { value: "jsm", label: "Jira Service Management" },
  { value: "freshservice", label: "Freshservice" },
  { value: "servicenow", label: "ServiceNow (OAuth)" },
  { value: "servicenow-serviceaccount", label: "ServiceNow (Service Account)" },
  { value: "solarwinds", label: "SolarWinds" },
  { value: "zendesk", label: "Zendesk" },
  // One board per job: its id goes in "source project".
  { value: "monday", label: "monday.com (one board)" },
  { value: "sysaid", label: "SysAid" },
];

export const FILE_FORMAT_PROVIDERS: { value: ImportProvider; label: string }[] = [
  { value: "harmony", label: "Harmony (pre-normalized)" },
  { value: "freshservice", label: "Freshservice" },
  { value: "jsm", label: "Jira Service Management" },
  { value: "servicenow", label: "ServiceNow" },
  { value: "solarwinds", label: "SolarWinds" },
  { value: "zendesk", label: "Zendesk" },
];

export type ImportJobStatus =
  | "pending"
  | "fetching"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled";

export const ACTIVE_STATUSES: ImportJobStatus[] = [
  "pending",
  "fetching",
  "processing",
];

export interface CreateImportJobRequest {
  source: ImportSource;
  target_desk_id?: string;
  target_subdesk_id?: string;
  credential_id?: string;
  provider?: ImportProvider;
  source_project?: string;
  /** Record kinds to fetch, e.g. ["change"]. Omit for every kind. */
  record_types?: string[];
  since_date?: string;
  until_date?: string;
  include_closed?: boolean;
  s3_keys?: string[];
  dry_run?: boolean;
  skip_dedup?: boolean;
  rate_limit?: number;
  max_tickets?: number;
  transform_code?: string;
  create_tickets?: boolean;
  generate_insights?: boolean;
  /** JSM/Jira provider imports only. Replays each ticket's changelog
   *  (status, priority, title, description edits) into the created Harmony
   *  ticket's activity timeline at the original historical timestamps. */
  include_audit_log?: boolean;
  /** Requires include_audit_log. Appends every custom field change from the
   *  changelog to the created ticket's description, oldest first. */
  include_custom_field_history?: boolean;
  /** JSM provider imports only. null/omitted appends every visible JSM custom
   *  field to the description; a list imports only the listed fields. */
  custom_field_mappings?: CustomFieldMapping[] | null;
  /** JSM/Jira provider imports only. Overrides the integration's
   *  sync_linked_tickets setting for this job; null/omitted inherits it. */
  sync_linked_tickets?: boolean | null;
  /** Writes the fetched tickets to one CSV file instead of creating them in
   *  Harmony. The API turns create_tickets off, makes target_desk_id optional
   *  and rejects the options that only shape created tickets. */
  export_csv?: boolean;
}

export type CustomFieldImportMethod = "harmony_custom_field" | "description";

export interface CustomFieldMapping {
  /** e.g. "customfield_10050" */
  source_field_id: string;
  source_field_name?: string | null;
  method: CustomFieldImportMethod;
  /** Required iff method is "harmony_custom_field". */
  harmony_field_name?: string | null;
}

export interface ImportJobResponse {
  id: string;
  tenant_id: string;
  source: ImportSource;
  credential_id: string | null;
  provider: ImportProvider | null;
  target_desk_id: string;
  target_subdesk_id: string | null;
  source_project: string | null;
  record_types: string[] | null;
  custom_field_mappings?: CustomFieldMapping[] | null;
  status: ImportJobStatus;
  total_tickets: number | null;
  total_batches: number | null;
  batches_processed: number;
  fetched_tickets: number;
  processed_tickets: number;
  succeeded_tickets: number;
  failed_tickets_count: number;
  skipped_tickets: number;
  dry_run: boolean;
  skip_dedup: boolean;
  include_closed: boolean;
  max_tickets: number | null;
  rate_limit: number | null;
  since_date: string | null;
  until_date: string | null;
  transform_code: string | null;
  s3_keys: string[] | null;
  create_tickets: boolean;
  generate_insights: boolean;
  include_audit_log: boolean;
  include_custom_field_history?: boolean;
  /** null inherits the integration's setting. */
  sync_linked_tickets?: boolean | null;
  export_csv?: boolean;
  insights_processed_tickets: number;
  insights_failed_tickets: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ImportJobListResponse {
  jobs: ImportJobResponse[];
  total: number;
  page: number;
  page_size: number;
}

export type TicketResultStatus = "succeeded" | "failed" | "skipped";

export interface ImportTicketResponse {
  provider_ticket_id: string;
  harmony_ticket_id: string | null;
  status: TicketResultStatus;
  skip_reason: string | null;
  error: string | null;
  created_at: string;
}

export interface ImportTicketListResponse {
  tickets: ImportTicketResponse[];
  total: number;
  page: number;
  page_size: number;
}

export interface UploadUrlRequest {
  filename: string;
}

export interface UploadUrlResponse {
  upload_url: string;
  s3_key: string;
}

export interface ExportUrlResponse {
  download_url: string;
  /** ISO datetime after which download_url stops working. */
  expires_at: string;
}
